import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { CloudArrowUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import { uploadFile, useFileUpload, useStore } from "../helpers";
import { useParams } from "react-router";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";
import { safe_get } from "../report-visuals";
import { toast } from "react-toastify";
import { LoadingState } from "../components/LoadingState";

export interface AudioSubmitItem {
    name: string;
    file: File;
}

export interface AudioFormValues {
    audios: {
        id?: string; // used internally by react-hook-form
        name: string;
        preview: string;
        fileName: string;
        fileSize: string;
    }[];
}

interface Props {
    loading?: boolean;
    onSubmit: (data: AudioSubmitItem[]) => void;
}

const AudioUpload = ({ loading: lo = false, onSubmit }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);

    // Maintain a parallel state array holding the pure, untouched File objects
    const [rawFiles, setRawFiles] = useState<File[]>([]);
    const { isUploading, uploadFileToS3 } = useFileUpload();
    const params = useParams();
    const projectid = params.projectid;
    const path = `project-${projectid}/notification/audio`;
    const {
        projectStore: { activeProject, editProjectProperties },
    } = useStore();
    const currentProperties = toJS(activeProject?.properties);
    const audioMessageProperty = useMemo(() => currentProperties?.audio_messages, []);
    const audioMessages = safe_get(audioMessageProperty, "value.messages", []);
    const [existingMessages, setExistingMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (audioMessageProperty) {
            setExistingMessages([...toJS(audioMessages)]);
        }
    }, [activeProject, audioMessageProperty, audioMessages, projectid]);

    useEffect(() => {
        console.log("Existing messages", toJS(existingMessages));
    }, [existingMessages]);
    const {
        register,
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<AudioFormValues>({
        defaultValues: {
            audios: [],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "audios",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;

        const files = Array.from(e.target.files);
        const validAudioFiles = files.filter((file) => file.type.startsWith("audio"));

        validAudioFiles.forEach((file) => {
            // Append file to raw files state
            setRawFiles((prev) => [...prev, file]);

            // Append metadata to form state
            append({
                name: file.name.replace(/\.[^/.]+$/, ""),
                preview: URL.createObjectURL(file),
                fileName: file.name,
                fileSize: (file.size / 1024 / 1024).toFixed(2),
            });
        });

        if (inputRef.current) inputRef.current.value = "";
    };

    const handleRemove = (index: number) => {
        URL.revokeObjectURL(fields[index].preview);

        // Remove from both states synchronized by index
        setRawFiles((prev) => prev.filter((_, i) => i !== index));
        remove(index);
    };

    const handleCancel = () => {
        fields.forEach((field) => URL.revokeObjectURL(field.preview));
        setRawFiles([]);
        remove();
    };

    const handleFormSubmit = async (data: AudioFormValues) => {
        try {
            setLoading(true);
            // 1. Map each item to a Promise that resolves to the final payload shape
            const uploadPromises = data.audios.map(async (audio, index) => {
                const fileToUpload = rawFiles[index];

                // Execute the S3 upload
                const uploadInfo = await uploadFileToS3(path, fileToUpload);

                // Return the combined payload containing the custom name and the remote URL
                return {
                    name: audio.name,
                    url: uploadInfo?.location || "", // Or whatever key your uploadInfo returns
                };
            });
            const submissionData = await Promise.all(uploadPromises);
            setExistingMessages([...existingMessages, ...submissionData]);

            let payload = {
                id: projectid,
                properties: {
                    ...currentProperties,
                    audio_messages: {
                        type: "json",
                        value: {
                            messages: [...existingMessages, ...submissionData],
                        },
                    },
                },
            };

            const response = await editProjectProperties(payload);
            if (response.ok) {
                toast.success("Added Successfully");
            }
            console.log(response, "response here");
            onSubmit(submissionData);
        } catch (error) {
            console.error("Failed to upload audio files:", error);
            // Handle error states here (e.g., showing a toast notification)
        } finally {
            setLoading(false);
        }
    };
    if (loading) {
        return <LoadingState />;
    }
    return (
        <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white shadow-sm"
        >
            <div className="border-b px-6 py-5">
                <h2 className="text-lg font-semibold text-gray-900">Upload Audio</h2>
                <p className="mt-1 text-sm text-gray-500">Upload existing audio files or record them.</p>
            </div>

            <div className="space-y-6 p-6">
                {/* Upload Dropzone */}
                <div
                    onClick={() => inputRef.current?.click()}
                    className="cursor-pointer rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-10 transition hover:border-indigo-500 hover:bg-indigo-50"
                >
                    <div className="flex flex-col items-center">
                        <CloudArrowUpIcon className="h-12 w-12 text-indigo-600" />
                        <p className="mt-4 font-medium text-gray-800">Click to upload audio</p>
                        <p className="mt-1 text-sm text-gray-500">MP3 • WAV • M4A</p>

                        <button
                            type="button"
                            className="mt-5 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                        >
                            Choose Files
                        </button>
                    </div>

                    <input
                        ref={inputRef}
                        hidden
                        type="file"
                        multiple
                        accept="audio/*"
                        onChange={handleChange}
                    />
                </div>

                {/* List of Uploaded Audios */}
                {fields.length > 0 && (
                    <div className="space-y-4">
                        {fields.map((field, index) => (
                            <div key={field.id} className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 space-y-2">
                                        <div className="flex flex-col">
                                            <label className="mb-1 text-sm font-semibold text-gray-800">
                                                Audio Name
                                            </label>
                                            <input
                                                type="text"
                                                {...register(`audios.${index}.name`, {
                                                    required: "Audio name is required",
                                                })}
                                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                                placeholder="Enter a name for this audio..."
                                            />
                                            {errors.audios?.[index]?.name && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {errors.audios[index]?.name?.message}
                                                </p>
                                            )}
                                        </div>

                                        <p className="text-xs text-gray-400">
                                            Original file: {field.fileName} ({field.fileSize} MB)
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => handleRemove(index)}
                                        className="rounded-lg p-2 text-red-500 transition hover:bg-red-100"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <audio controls className="mt-4 w-full">
                                    <source src={field.preview} />
                                </audio>
                            </div>
                        ))}
                    </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={handleCancel}
                        // disabled={fields.length === 0 || loading}
                        className="rounded-lg border border-gray-300 px-5 py-2 font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        // disabled={fields.length === 0 || loading}
                        className="rounded-lg bg-indigo-600 px-6 py-2 font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                        {loading ? "Uploading..." : "Ok"}
                    </button>
                </div>
            </div>
            {existingMessages.length > 0 && (
                <div className="p-4">
                    <h1>Existing Audios</h1>
                    {existingMessages?.map((msg) => {
                        return (
                            <div
                                key={msg?.url}
                                className="my-2 flex items-center justify-between rounded-lg bg-gray-200 p-2"
                            >
                                {msg?.name}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setExistingMessages([
                                            ...existingMessages.filter((m) => m?.url !== msg?.url),
                                        ])
                                    }
                                    className="rounded-lg p-2 text-red-500 transition hover:bg-red-100"
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </form>
    );
};

export default observer(AudioUpload);
